import Layout from '@app-studio-web/ui/components/Layout'
import * as Sentry from '@sentry/react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

type ButtonOption = {
  text: React.ReactNode | string
  subtext?: string
  value: string
  icon: string
}

export default function QuestionCommitmentPage() {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [clickedValue, setClickedValue] = useState<string | null>(null)

  const heading = t('pages.onboarding.commitment.heading')
  const buttons: ButtonOption[] = useMemo(
    () => [
      {
        text: t('pages.onboarding.commitment.option1'),
        subtext: t('pages.onboarding.commitment.option1Sub'),
        value: '2min',
        icon: '⚡',
      },
      {
        text: t('pages.onboarding.commitment.option2'),
        subtext: t('pages.onboarding.commitment.option2Sub'),
        value: '5min',
        icon: '🌟',
      },
      {
        text: t('pages.onboarding.commitment.option3'),
        subtext: t('pages.onboarding.commitment.option3Sub'),
        value: '10min',
        icon: '🚀',
      },
    ],
    [t],
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'answer'
    hiddenInput.value = clickedValue || ''
    event.currentTarget.appendChild(hiddenInput)

    if (clickedValue) {
      try {
        sessionStorage.setItem('onboarding_commitment', clickedValue)
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    submit(event.currentTarget)
    event.currentTarget.removeChild(hiddenInput)
  }

  return (
    <Layout>
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#FFFFFF] to-[#EFFBFF]" />
      <div className="flex h-full flex-col">
        <div className="mt-11 shrink-0 text-center">
          <h1 className="custom-heading mb-4" data-testid="label-header">
            {heading}
          </h1>
        </div>

        <div className="flex grow items-end justify-center pb-10">
          <Form
            method="post"
            className="w-full max-w-md"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col space-y-3">
              {buttons.map((button, index) => (
                <button
                  key={`button-${index}`}
                  type="submit"
                  className={`flex w-full cursor-pointer items-center gap-4 rounded-[20px] border bg-white px-6 py-5 text-left text-base transition-all hover:shadow-md ${
                    clickedValue === button.value
                      ? 'border-2 border-blue-400 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                  data-testid={`button-option-${index + 1}`}
                  onClick={() => setClickedValue(button.value)}
                >
                  <span className="text-2xl">{button.icon}</span>
                  <div>
                    <p className="font-semibold">{button.text}</p>
                    {button.subtext && (
                      <p className="text-sm text-gray-500">{button.subtext}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Form>
        </div>
      </div>
    </Layout>
  )
}
